import { Flex } from "@chakra-ui/layout";
import { Text } from "@chakra-ui/react";
import { useFindConta } from "features/conta/hooks";
import { PagarContaForm } from "features/conta/typings";
import moment from "moment";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import AnexarArquivos from "./components/AnexarArquivos";
import FooterModal from "components/Button/FooterModal";
import SkeletonForm from "./SkeletonForm";
import useFindContaEncargosAtraso from "core/features/conta/hooks/useFindContaEncargos";
import { useDebounce } from "features/shared/useDebouce";
import { OperacaoEnum } from "core/resources/enums/conta";
import FormLiquidarConta from "./components/FormLiquidarConta";

interface FormPagarContaProps {
  contaId: string;
  visible?: boolean;
  operacao: OperacaoEnum;
  onSave?: (data: PagarContaForm) => any;
  onClose?: () => any;
  refetch?: () => any;
  isLoadingSave?: boolean;
  dataPagamentoPadraoIgualADataVencimento?: boolean;
}

function FormPagarConta({
  onSave,
  onClose,
  operacao,
  contaId,
  isLoadingSave,
  dataPagamentoPadraoIgualADataVencimento = false,
}: FormPagarContaProps) {
  const [arquivosExcluidos, setArquivosExcluidos] = useState<string[]>([]);
  const { data, isFetching } = useFindConta(contaId);
  const [registerAnother, setRegisterAnother] = useState(false);
  const { control, watch, reset, formState, setValue, handleSubmit } =
    useForm<PagarContaForm>({
      mode: "all",
    });

  const form = watch();

  const debouncedDataPagamento = useDebounce(form.dataPagamento, 150);

  const {
    data: dataEncargos,
    isLoading: isLoadingEncargos,
    isFetching: isFetchingEncargos,
  } = useFindContaEncargosAtraso(
    contaId,
    debouncedDataPagamento,
    data?.operacao === OperacaoEnum.Receita
  );

  useEffect(() => {
    if (dataEncargos) {
      setValue("juros", dataEncargos.juros);
      setValue("multa", dataEncargos.multa);
    }
  }, [dataEncargos, setValue]);

  useEffect(() => {
    if (data) {
      reset({
        id: data.id,
        formaPagamentoId: data.formaPagamentoId,
        tenantContaId: data.tenantContaId,
        valor: data.valor,
        dataPagamento: data.dataPagamento
          ? moment(data.dataPagamento).format("YYYY-MM-DD")
          : dataPagamentoPadraoIgualADataVencimento
          ? moment(data.dataVencimento).format("YYYY-MM-DD")
          : moment().format("YYYY-MM-DD"),
        multa: data.multa,
        juros: data.juros,
        anexarArquivos: !!data.anexos?.length,
        files:
          data.anexos?.map((anexo) => ({
            id: anexo.id,
            nome: anexo.nome,
            referencia: anexo.referencia,
            mimetype: anexo.mimetype,
            tipoAnexoId: anexo.tipoAnexoId,
          })) ?? [],
      });
    }
  }, [data, dataPagamentoPadraoIgualADataVencimento, reset]);

  useEffect(() => {
    const valorPago =
      form.valor +
      form.juros +
      form.multa +
      (form?.taxaAdicionalCategoriaOperacao === OperacaoEnum.Receita
        ? form?.taxaAdicional ?? 0
        : 0) -
      (form?.taxaRegistro ?? 0);

    setValue("valorPago", valorPago);
  }, [
    form.valor,
    form.juros,
    form.multa,
    form?.taxaAdicionalCategoriaOperacao,
    form?.taxaAdicional,
    setValue,
    form?.taxaRegistro,
  ]);

  const onSubmit = async (data: PagarContaForm) => {
    onSave?.({ ...data, arquivosExcluidos });
  };

  if (isFetching) {
    return <SkeletonForm />;
  }

  return (
    <Flex flexDir="column">
      <Title label="Liquidar conta" />

      <FormLiquidarConta
        control={control}
        isFetchingEncargos={isFetchingEncargos}
        isLoadingEncargos={isLoadingEncargos}
        operacao={operacao}
        form={form}
        integradoPagamentoOnline={dataEncargos?.tenantIntegradoPagamentoOnline}
        setValue={setValue}
      />

      <AnexarArquivos
        control={control}
        form={form}
        setValue={setValue as any}
        pushArquivoRemovido={(id) =>
          setArquivosExcluidos((state) => [...state, id])
        }
      />

      <FooterModal
        isDisabled={
          !formState.isValid || isLoadingEncargos || isFetchingEncargos
        }
        onSubmit={handleSubmit(onSubmit)}
        registerAnother={registerAnother}
        setRegisterAnother={setRegisterAnother}
        isLoadingSave={isLoadingSave || isLoadingEncargos || isFetchingEncargos}
        primaryButtonText="Liquidar"
        showRegisterAnother={false}
        onClose={onClose}
      />
    </Flex>
  );
}

const Title = ({ label }: { label: string }) => (
  <Text fontSize={18} fontWeight="bold" mb={6}>
    {label}
  </Text>
);

export default FormPagarConta;
